import React, { useState } from "react";
import { FiCheck, FiX, FiEdit2 } from "react-icons/fi";

interface EditableCellProps {
  displayValue?: string;
  error?: string[];
  options?: Array<{ label: string; value: string | number; }>;
  required?: boolean;
  type?: "text" | "select" | "textarea";
  value: string | number;
  onChange: (value: string | number) => void;
}

export const EditableCell: React.FC<EditableCellProps> = ({
  value,
  displayValue,
  onChange,
  error,
  type = "text",
  options,
  required = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    // For select inputs, convert back to number if the original value was a number
    const finalValue = type === "select" && typeof value === "number"
      ? Number(editValue)
      : editValue;
    onChange(finalValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && type !== "textarea") {
      handleSave();
    }
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  const hasError = error && error.length > 0;

  if (!isEditing) {
    const effectiveDisplayValue = displayValue !== undefined ? displayValue : value;
    const displayText = effectiveDisplayValue || "-";

    return (
      <div className="group relative">
        <div
          className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1 transition-colors hover:bg-gray-100 ${hasError ? "border border-red-300 bg-red-50" : ""
            }`}
          onClick={() => setIsEditing(true)}
        >
          <span className={`flex-1 ${hasError ? "text-red-600" : "text-gray-900"}`}>
            {displayText}
          </span>
          <FiEdit2 className="h-3 w-3 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
        {hasError && (
          <div className="mt-1 text-xs text-red-600">{error.join(", ")}</div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        {type === "select" && options ? (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            className={`flex-1 rounded border px-2 py-1 text-sm focus:border-blue-500 focus:outline-none ${hasError ? "border-red-500" : "border-gray-300"
              }`}
            autoFocus
            onKeyDown={handleKeyDown}
          >
            {options.map((opt) => (
              <option key={opt.value} value={String(opt.value)}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : type === "textarea" ? (
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={`flex-1 rounded border px-2 py-1 text-sm focus:border-blue-500 focus:outline-none ${hasError ? "border-red-500" : "border-gray-300"
              }`}
            rows={3}
            autoFocus
            onKeyDown={handleKeyDown}
          />
        ) : (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={`flex-1 rounded border px-2 py-1 text-sm focus:border-blue-500 focus:outline-none ${hasError ? "border-red-500" : "border-gray-300"
              }`}
            autoFocus
            onKeyDown={handleKeyDown}
          />
        )}
        <button
          onClick={handleSave}
          className="text-green-600 hover:text-green-800"
          title="Save"
        >
          <FiCheck className="h-4 w-4" />
        </button>
        <button
          onClick={handleCancel}
          className="text-gray-500 hover:text-gray-700"
          title="Cancel"
        >
          <FiX className="h-4 w-4" />
        </button>
      </div>
      {hasError && (
        <div className="mt-1 text-xs text-red-600">{error.join(", ")}</div>
      )}
    </div>
  );
};
